// Offline progress. On load we credit the settlement for time away at a reduced
// efficiency, capped (default 8h, extendable via the Long Memory skill). The cap
// exists to create a reason to come back rather than to punish. We do not
// advance the wave clock offline, so nobody returns to an instant defeat.

import type { GameState } from '@core/state';
import { BALANCE } from '@data/balance';
import { RESOURCE_ORDER, type ResourceId } from '@data/resources';
import { computeIncome, progressValue } from './stats';

export interface OfflineReport {
  seconds: number;
  gained: Partial<Record<ResourceId, number>>;
}

/** Applies offline accrual and returns a report (null if the gap was trivial).
 *  Skipped entirely while a card choice or death screen is pending, since the
 *  run was effectively paused. */
export function applyOffline(state: GameState): OfflineReport | null {
  if (state.pending.dead || state.pending.cardChoices) {
    state.lastSavedWall = Date.now();
    return null;
  }

  const now = Date.now();
  const elapsed = (now - state.lastSavedWall) / 1000;
  state.lastSavedWall = now;
  if (elapsed < BALANCE.offline.minSecondsToReport) return null;

  const capSeconds = state.metaPerks.offlineCapHours * 3600;
  const effectiveSeconds = Math.min(elapsed, capSeconds) * BALANCE.offline.efficiency;

  // Timed buffs (Surge) are short real-time spikes, but gameTime is frozen while
  // away so updateBuffs never expires them here. Drop any buff whose remaining
  // duration is shorter than the gap we are crediting, so offline income is
  // steady-state, not the whole capped window multiplied by the buff.
  state.run.buffs = state.run.buffs.filter((b) => b.expiresAt - state.gameTime > elapsed);

  const income = computeIncome(state);
  const gained: Partial<Record<ResourceId, number>> = {};
  let any = false;
  for (const id of RESOURCE_ORDER) {
    const g = income[id] * effectiveSeconds;
    if (g > 0) {
      state.run.resources[id] += g;
      gained[id] = g;
      any = true;
    }
  }

  if (!any) return null;

  // Offline production counts toward age progress exactly like online income
  // (production.ts credits the same computeIncome output). The wave clock stays
  // frozen, so if this pushes past the age threshold the gate boss is merely
  // summoned as the NEXT wave, never an instant defeat on return.
  state.run.progress += progressValue(gained);

  const report: OfflineReport = { seconds: Math.min(elapsed, capSeconds), gained };
  return report;
}
